import { useLayoutEffect, useRef, useState } from 'react';
import type { Icon } from '@phosphor-icons/react';
import { AddressBook, Check, Database, EnvelopeSimple, Files, Headset, Briefcase, Receipt, Sparkle } from '@phosphor-icons/react';
import type { SiteContent } from '../../content/site';
import { boxIn } from './motion';
import type { Box } from './motion';
import { paintThread } from './threads';

type Territory = SiteContent['manifesto']['territory'];

/** A raised sheet, a touch lighter than Mineral White (never pure white), as in AgentDemo. */
const SHEET =
	'border border-graphite/[0.07] bg-[#FBFBF7] shadow-[0_18px_40px_-28px_rgba(41,77,59,0.5)] dark:border-mineral/10 dark:bg-forest-deep dark:shadow-none';

/** One quiet pictogram per data source, matched on the label (accents and case ignored). */
const ICONS: Array<[string, Icon]> = [
	['e-mail', EnvelopeSimple],
	['crm', AddressBook],
	['factur', Receipt],
	['support', Headset],
	['document', Files],
];

const normalize = (text: string) =>
	text
		.normalize('NFD')
		.replace(/[̀-ͯ]/g, '')
		.toLowerCase();

function iconFor(label: string): Icon {
	const key = normalize(label);
	return ICONS.find(([word]) => key.startsWith(word))?.[1] ?? Database;
}

type Paths = { into: string[]; out: string[]; up: string[] };

/** Spread (px) of the strands where several threads meet one node. */
const SPREAD_IN = 11;
const SPREAD_OUT = 14;
const SPREAD_UP = 7;
/** Stacked signals (phones): lane offset in the side gutters and the corner radius of the turns. */
const LANE = 4;
const TURN = 9;

const centerX = (box: Box) => box.x + box.w / 2;
const centerY = (box: Box) => box.y + box.h / 2;
const offset = (index: number, count: number) => index - (count - 1) / 2;

/** A vertical S between two points (leaves and lands vertically). */
const sCurve = (x0: number, y0: number, x1: number, y1: number) => {
	const mid = (y0 + y1) / 2;
	return `C ${x0} ${mid}, ${x1} ${mid}, ${x1} ${y1}`;
};

/**
 * The threads of the territory, from measured node boxes (in the field's coordinates).
 * Sources converge into the agents; the agents emit one thread per signal; the
 * signals converge into the human validation. Threads end under the opaque nodes.
 *
 * Signals side by side: plain vertical S curves. Signals stacked (phones): the agents'
 * threads run down the left gutter and turn into each signal; the signals leave
 * through the right gutter towards the validation. Lanes are ordered so no two
 * threads cross.
 */
function buildPaths(sources: Box[], core: Box, signals: Box[], human: Box): Paths {
	const into = sources.map((source, i) => {
		const x0 = centerX(source);
		const y0 = source.y + source.h + 4;
		const x1 = centerX(core) + offset(i, sources.length) * SPREAD_IN;
		return `M ${x0} ${y0} ${sCurve(x0, y0, x1, core.y + 2)}`;
	});

	const count = signals.length;
	const row = signals.every((signal) => Math.abs(signal.y - (signals[0]?.y ?? 0)) < 2);
	const hx = (j: number) => centerX(human) + offset(j, count) * SPREAD_UP;
	const hy = centerY(human);

	if (row) {
		return {
			into,
			out: signals.map((signal, j) => {
				const x0 = centerX(core) + offset(j, count) * SPREAD_OUT;
				const y0 = core.y + core.h - 2;
				return `M ${x0} ${y0} ${sCurve(x0, y0, centerX(signal), signal.y)}`;
			}),
			up: signals.map((signal, j) => {
				const x0 = centerX(signal);
				const y0 = signal.y + signal.h - 2;
				return `M ${x0} ${y0} ${sCurve(x0, y0, hx(j), hy)}`;
			}),
		};
	}

	const first = signals[0];
	const last = signals[count - 1];
	if (!first || !last) return { into, out: [], up: [] };
	const y0 = core.y + core.h - 2;
	const reach = Math.max(TURN, first.y - y0);
	const bottom = last.y + last.h;

	return {
		into,
		out: signals.map((signal, j) => {
			// The first signal turns first, so it takes the lane nearest the signals.
			const o = -offset(j, count);
			const x0 = centerX(core) + o * SPREAD_OUT;
			const lane = signal.x - 12 + o * LANE;
			const y = centerY(signal);
			return [
				`M ${x0} ${y0}`,
				`C ${x0} ${y0 + reach * 0.6}, ${lane} ${y0 + reach * 0.4}, ${lane} ${y0 + reach}`,
				`L ${lane} ${y - TURN}`,
				`C ${lane} ${y - TURN * 0.45}, ${lane + TURN * 0.55} ${y}, ${lane + TURN} ${y}`,
				`L ${signal.x} ${y}`,
			].join(' ');
		}),
		up: signals.map((signal, j) => {
			// The first signal joins first, so it takes the outer lane.
			const o = -offset(j, count);
			const right = signal.x + signal.w;
			const lane = right + 12 + o * LANE;
			const y = centerY(signal);
			const x1 = hx(count - 1 - j);
			return [
				`M ${right - 2} ${y}`,
				`L ${lane - TURN} ${y}`,
				`C ${lane - TURN * 0.55} ${y}, ${lane} ${y + TURN * 0.45}, ${lane} ${y + TURN}`,
				`L ${lane} ${bottom + 4}`,
				sCurve(lane, bottom + 4, x1, hy),
			].join(' ');
		}),
	};
}

const EMPTY = 'M 0 0';

/**
 * "Toute l'entreprise": a general, code-drawn demonstration (no project, no capture).
 * The data of a territory (energy, mobility, water, buildings, weather) flows along
 * fine threads into the AI agents; the agents emit what they detect or anticipate
 * (solar tips: the single accent); every signal then reaches the people who decide.
 *
 * Default markup = final state (every thread drawn, every node shown, validation
 * checked): reduced motion shows it as is. territoryFlow (motion.ts) replays it.
 * Everything readable is real text in reading order (lists of sources and signals,
 * the agents, the validation); the threads are decorative and aria-hidden.
 *
 * Hooks: data-territory-core (the node the section's camera zooms out from, always
 * visible), -source, -thread (in, out, up), -pulse, -signal, -human, -check, -ring.
 *
 * fill: the diagram takes the height of a fixed stage (pinned scene), the gaps
 * between the tiers stretch; otherwise the gaps have their own height.
 */
export function TerritoryView({ territory, fill = false }: { territory: Territory; fill?: boolean }) {
	const { label, sources, core, coreText, signals, human } = territory;
	const field = useRef<HTMLDivElement>(null);
	const [paths, setPaths] = useState<Paths>(() => ({
		into: sources.map(() => EMPTY),
		out: signals.map(() => EMPTY),
		up: signals.map(() => EMPTY),
	}));

	useLayoutEffect(() => {
		const root = field.current;
		if (!root) return undefined;
		const pick = (selector: string) => Array.from(root.querySelectorAll<HTMLElement>(selector));
		const measure = () => {
			const coreEl = pick('[data-territory-core]')[0];
			const humanEl = pick('[data-territory-human]')[0];
			if (!coreEl || !humanEl) return;
			const next = buildPaths(
				pick('[data-territory-source]').map((el) => boxIn(el, root)),
				boxIn(coreEl, root),
				pick('[data-territory-signal]').map((el) => boxIn(el, root)),
				boxIn(humanEl, root),
			);
			setPaths((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
		};
		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(root);
		pick('[data-territory-node]').forEach((node) => observer.observe(node));
		let alive = true;
		document.fonts?.ready.then(() => {
			if (alive) measure();
		});
		return () => {
			alive = false;
			observer.disconnect();
		};
	}, []);

	// New geometry: repaint each thread's current window in pixels of its new length.
	useLayoutEffect(() => {
		field.current?.querySelectorAll<SVGPathElement>('[data-territory-thread]').forEach(paintThread);
	}, [paths]);

	const gap = fill ? 'min-h-9 max-h-28 flex-1' : 'h-12 sm:h-14';
	const threads = (kind: keyof Paths, tip: boolean) =>
		paths[kind].map((d, index) => (
			<g key={`${kind}-${index}`}>
				<path d={d} strokeWidth={1} className="stroke-graphite/[0.1] dark:stroke-mineral/[0.1]" />
				<path
					data-territory-thread={kind}
					data-a="0"
					data-b="1"
					d={d}
					strokeWidth={1.25}
					strokeLinecap="round"
					className="stroke-forest/[0.55] dark:stroke-sage/[0.6]"
				/>
				{tip && <path d={d} stroke="#E8C96A" strokeWidth={2.5} strokeLinecap="round" />}
			</g>
		));

	return (
		<figure className={`mx-auto flex w-full max-w-[38rem] flex-col ${fill ? 'h-full' : ''}`}>
			<figcaption className="flex items-center gap-2 text-[13px] font-medium leading-none text-graphite-muted dark:text-dusk-muted">
				<Briefcase aria-hidden size={15} weight="bold" className="text-forest dark:text-sage" />
				{label}
			</figcaption>

			<div
				ref={field}
				data-territory-field
				className={`relative mt-7 flex flex-col ${fill ? 'min-h-0 flex-1 justify-center' : ''}`}
			>
				<svg aria-hidden className="pointer-events-none absolute inset-0 h-full w-full overflow-visible" fill="none">
					{threads('into', false)}
					{threads('out', true)}
					{threads('up', false)}
				</svg>

				{/* The data of the territory. */}
				<ul className="relative grid grid-cols-5 gap-x-1">
					{sources.map((source) => {
						const SourceIcon = iconFor(source);
						return (
							<li
								key={source}
								data-territory-source
								data-territory-node
								className="relative flex flex-col items-center gap-2"
							>
								<span
									aria-hidden
									className={`grid h-10 w-10 place-items-center rounded-[12px] text-forest dark:text-sage ${SHEET}`}
								>
									<SourceIcon size={18} />
								</span>
								<span className="whitespace-nowrap font-mono text-[10px] font-medium leading-none tracking-[-0.02em] text-graphite-muted min-[380px]:text-[11px] dark:text-dusk-muted">
									{source}
								</span>
							</li>
						);
					})}
				</ul>

				<div aria-hidden className={gap} />

				{/* The agents: where the scene's camera lands. */}
				<div className="relative flex justify-center">
					<div
						data-territory-core
						data-territory-node
						className="relative w-full max-w-[17rem] rounded-[16px] bg-forest px-5 py-4 text-mineral shadow-[0_22px_44px_-26px_rgba(41,77,59,0.75)] dark:shadow-none dark:ring-1 dark:ring-inset dark:ring-mineral/10"
					>
						<span
							data-territory-pulse
							aria-hidden
							className="pointer-events-none absolute -inset-[3px] rounded-[19px] border-2 border-solar opacity-0"
						/>
						<p className="flex items-center gap-2 font-display text-[17px] font-bold leading-none tracking-[-0.01em]">
							<Sparkle aria-hidden size={15} weight="fill" className="text-solar" />
							{core}
						</p>
						<p className="mt-2 text-pretty text-[13px] leading-snug text-mineral/80">{coreText}</p>
					</div>
				</div>

				<div aria-hidden className={gap} />

				{/* What they detect or anticipate. */}
				<ul className="relative grid gap-2.5 px-7 sm:grid-cols-3 sm:gap-3 sm:px-0">
					{signals.map((signal) => (
						<li
							key={signal}
							data-territory-signal
							data-territory-node
							className={`relative rounded-[12px] px-3.5 py-2.5 text-pretty text-[13px] font-medium leading-snug text-graphite dark:text-mineral ${SHEET}`}
						>
							{signal}
						</li>
					))}
				</ul>

				<div aria-hidden className={gap} />

				{/* The people who decide. */}
				<div className="relative flex justify-center">
					<p
						data-territory-human
						data-territory-node
						className={`relative inline-flex items-center gap-2.5 rounded-full py-1.5 pl-1.5 pr-4 text-[14px] font-medium leading-none text-graphite dark:text-mineral ${SHEET}`}
					>
						<span aria-hidden className="relative grid h-7 w-7 shrink-0 place-items-center">
							<span data-territory-check className="absolute inset-0 grid place-items-center rounded-full bg-forest text-mineral">
								<Check size={13} weight="bold" />
							</span>
							<span
								data-territory-ring
								className="pointer-events-none absolute inset-0 rounded-full border-2 border-solar opacity-0"
							/>
						</span>
						{human}
					</p>
				</div>
			</div>
		</figure>
	);
}
